import { motion } from "framer-motion";
import {
  ArrowRight,
  ClipboardCheck,
  FileText,
  Layers,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  UsersRound
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { StatusPill } from "@/components/StatusPill";
import { useAdminWorkspace } from "@/pages/admin/AdminWorkspaceLayout";
import { EmptyState, formatDisplayDate } from "@/pages/admin/AdminRoutePages";

export function AdminDashboardPage() {
  const {
    dashboard,
    draftTemplateVersions,
    loadDashboard,
    pendingUsers,
    publishedTemplateVersions
  } = useAdminWorkspace();

  const recentSubmissions = dashboard.submissions.slice(0, 5);

  return (
    <>
      <motion.div
        animate={{ opacity: 1, y: 0 }}
        className="surface px-6 py-6 md:px-7"
        data-testid="admin-dashboard-hero"
        initial={{ opacity: 0, y: 18 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <p className="eyebrow">Administrator</p>
            <h1
              className="mt-3 text-3xl font-semibold tracking-tight text-ink md:text-4xl"
              data-testid="admin-heading"
            >
              Admin dashboard
            </h1>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-slate">
              Manage candidate access, agreement records, templates, and audit activity from one
              secure legal portal workspace.
            </p>
          </div>

          <button
            className="btn-secondary px-4 py-2.5"
            data-testid="admin-refresh-data"
            onClick={() => void loadDashboard()}
            type="button"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh Data
          </button>
        </div>
      </motion.div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <DashboardMetricCard label="Total Candidates" value={dashboard.summary.totalCandidates} />
        <DashboardMetricCard label="Pending Approval" value={dashboard.summary.pendingApproval} />
        <DashboardMetricCard
          label="Submitted Agreements"
          value={dashboard.summary.submittedAgreements}
        />
        <DashboardMetricCard
          label="Pending Agreement Review"
          value={dashboard.summary.pendingAgreementApprovals}
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        <section className="surface p-6" data-testid="admin-dashboard-card">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="eyebrow">Work Queue</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-ink">
                Review and manage portal records
              </h2>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-slate">
                Start with access approvals, then manage agreement forms and submitted records from
                the dedicated admin sections.
              </p>
            </div>
            <Link className="btn-primary px-4 py-2.5" to="/admin/users?view=pending">
              Review pending
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <DashboardActionCard
              description="Approve or reject verified accounts waiting for access."
              icon={<ClipboardCheck className="h-5 w-5" />}
              label="Pending approvals"
              stat={`${pendingUsers.length}`}
              testId="admin-quick-route-pending-users"
              to="/admin/users?view=pending"
            />
            <DashboardActionCard
              description="Review agreements submitted by candidates and approve or reject them."
              icon={<ShieldAlert className="h-5 w-5" />}
              label="Pending agreements"
              stat={`${dashboard.summary.pendingAgreementApprovals}`}
              testId="admin-quick-route-pending-agreements"
              to="/admin/pending-agreements"
            />
            <DashboardActionCard
              description="Search candidate profiles and review account status."
              icon={<UsersRound className="h-5 w-5" />}
              label="Candidate directory"
              stat={`${dashboard.users.length}`}
              testId="admin-quick-route-users"
              to="/admin/users"
            />
            <DashboardActionCard
              description="Confirm approved accounts ready for agreement completion."
              icon={<ShieldCheck className="h-5 w-5" />}
              label="Approved access"
              stat={`${dashboard.summary.approvedCandidates}`}
              testId="admin-quick-route-verified-approvals"
              to="/admin/verified-approvals"
            />
            <DashboardActionCard
              description="Review published and draft agreement templates."
              icon={<FileText className="h-5 w-5" />}
              label="Agreement forms"
              stat={`${dashboard.template.versions?.length ?? 0}`}
              testId="admin-quick-route-all-forms"
              to="/admin/all-forms"
            />
            <DashboardActionCard
              description="View every candidate's internship and addendum agreement side by side. Approve or reject each individually."
              icon={<Layers className="h-5 w-5" />}
              label="Candidate agreements"
              stat={`${dashboard.users.length}`}
              testId="admin-quick-route-candidate-agreements"
              to="/admin/candidate-agreements"
            />
          </div>

          <div className="mt-4">
            <Link
              className="inline-flex items-center gap-2 text-sm font-semibold text-royal transition hover:text-navy"
              data-testid="admin-quick-route-create-form"
              to="/admin/create-form"
            >
              Create a new form draft
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

      <aside className="surface p-6">
        <p className="eyebrow">Today</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
          Items needing attention
        </h2>
        <div className="mt-5 grid gap-3">
          <QueueMetric
            label="Candidate approvals"
            to="/admin/users?view=pending"
            value={pendingUsers.length}
          />
          <QueueMetric
            label="Agreement reviews"
            to="/admin/pending-agreements"
            value={dashboard.summary.pendingAgreementApprovals}
          />
          <QueueMetric
            label="Candidate agreements"
            to="/admin/candidate-agreements"
            value={dashboard.users.length}
          />
          <QueueMetric
            label="Draft form versions"
            to="/admin/all-forms"
            value={draftTemplateVersions.length}
          />
          <QueueMetric
            label="Published form versions"
            to="/admin/all-forms"
            value={publishedTemplateVersions.length}
          />
        </div>
      </aside>

      <section className="surface p-6 lg:col-span-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="eyebrow">Recent Submissions</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-ink">
              Latest agreement records
            </h2>
          </div>
        </div>

        <div className="mt-5 divide-y divide-line/70 rounded-[24px] border border-line/70 bg-white/70">
          {recentSubmissions.length === 0 ? (
            <div className="p-4">
              <EmptyState text="No agreement submissions are available yet." />
            </div>
          ) : (
            recentSubmissions.map((submission) => (
              <div
                className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                key={submission.id}
              >
                <div>
                  <p className="font-semibold text-ink">{submission.candidate.fullName}</p>
                  <p className="mt-1 text-sm leading-6 text-slate">
                    {submission.candidate.email}
                  </p>
                  {submission.sourceSubmissionId ? (
                    <p className="mt-1 text-xs font-semibold text-amber-800">
                      Updated submission (candidate changed details)
                    </p>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-3 sm:justify-end">
                  <StatusPill status={submission.status} />
                  <span className="text-sm text-slate">
                    {formatDisplayDate(submission.submittedAt ?? submission.updatedAt)}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      </div>
    </>
  );
}

function DashboardMetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="surface px-5 py-5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate/70">
        {label}
      </p>
      <p className="mt-3 text-3xl font-semibold tracking-tight text-ink">{value}</p>
    </div>
  );
}

function DashboardActionCard({
  description,
  icon,
  label,
  stat,
  testId,
  to
}: {
  description: string;
  icon: ReactNode;
  label: string;
  stat: string;
  testId?: string;
  to: string;
}) {
  return (
    <Link
      className="group rounded-[24px] border border-line/70 bg-cloud/55 p-5 transition duration-200 hover:border-royal/25 hover:bg-white"
      data-testid={testId}
      to={to}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-[18px] bg-white text-royal shadow-sm">
          {icon}
        </div>
        <ArrowRight className="mt-1 h-4 w-4 text-slate/60 transition group-hover:translate-x-0.5 group-hover:text-royal" />
      </div>
      <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate/70">
        {label}
      </p>
      <p className="mt-2 text-3xl font-semibold tracking-tight text-ink">{stat}</p>
      <p className="mt-3 text-sm leading-7 text-slate">{description}</p>
    </Link>
  );
}

function QueueMetric({ label, to, value }: { label: string; to: string; value: number }) {
  return (
    <Link
      className="flex items-center justify-between gap-4 rounded-[20px] border border-line/70 bg-cloud/55 px-4 py-3 transition hover:bg-white"
      to={to}
    >
      <span className="text-sm font-medium text-slate">{label}</span>
      <span className="text-lg font-semibold text-ink">{value}</span>
    </Link>
  );
}
